import { useState, useEffect, useRef, useCallback } from "react";
import { X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const LUCIDE_CDN_URL =
  "https://unpkg.com/lucide@0.541.0/dist/umd/lucide.min.js";

// Curated set of popular icons shown before any search

interface LucideGlobal {
  icons: Record<string, unknown>;
  createElement: (iconData: unknown) => SVGSVGElement;
}

let _lucidePromise: Promise<void> | null = null;

function ensureLucideLoaded(): Promise<void> {
  if ((window as unknown as Record<string, unknown>)["lucide"])
    return Promise.resolve();
  if (_lucidePromise) return _lucidePromise;
  _lucidePromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = LUCIDE_CDN_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Lucide CDN failed to load"));
    document.head.appendChild(script);
  });
  return _lucidePromise;
}

function getLucide(): LucideGlobal | null {
  return (
    ((window as unknown as Record<string, unknown>)[
      "lucide"
    ] as LucideGlobal) || null
  );
}

interface IconPickerPopupProps {
  open: boolean;
  onSelect: (iconName: string) => void;
  onClose: () => void;
}

function IconPreview({ name }: { name: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const lucide = getLucide();
    if (!lucide) return;

    const iconData = lucide.icons[name];
    if (!iconData) {
      container.textContent = "?";
      return;
    }

    const svg = lucide.createElement(iconData);
    svg.setAttribute("width", "20");
    svg.setAttribute("height", "20");
    svg.setAttribute("stroke", "currentColor");
    // Clear existing children safely (no innerHTML)
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(svg);
  }, [name]);

  return (
    <div
      ref={ref}
      className="w-5 h-5 flex items-center justify-center text-foreground"
    />
  );
}

export function IconPickerPopup({
  open,
  onSelect,
  onClose,
}: IconPickerPopupProps) {
  const [search, setSearch] = useState("");
  const [lucideReady, setLucideReady] = useState(false);
  const [allIconNames, setAllIconNames] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load Lucide CDN when popup opens
  useEffect(() => {
    if (!open) return;
    setSearch("");
    ensureLucideLoaded().then(() => {
      setLucideReady(true);
      const lucide = getLucide();
      if (lucide) {
        setAllIconNames(Object.keys(lucide.icons));
      }
    });
  }, [open]);

  // Auto-focus search input
  useEffect(() => {
    if (open && lucideReady) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [open, lucideReady]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const displayedIcons = useCallback(() => {
    if (!search.trim()) return allIconNames;
    const q = search.toLowerCase();
    return allIconNames.filter((n) => n.toLowerCase().includes(q));
  }, [search, allIconNames]);

  if (!open) return null;

  const icons = displayedIcons();

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] max-h-[520px] bg-background border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <h3 className="text-sm font-semibold text-foreground">
            Choose an icon
          </h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 cursor-pointer text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search icons..."
              className="h-8 pl-8 text-xs bg-background border-border"
            />
          </div>
        </div>

        {/* Icon Grid */}
        <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4">
            {!lucideReady ? (
              <div className="flex items-center justify-center py-12 text-xs text-muted-foreground">
                Loading icons...
              </div>
            ) : icons.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-xs text-muted-foreground">
                No icons found for &quot;{search}&quot;
              </div>
            ) : (
              <>
                {!search.trim() && (
                  <p className="text-[10px] text-muted-foreground mb-2 uppercase tracking-wider">
                    All Icons ({icons.length})
                  </p>
                )}
                <div className="grid grid-cols-6 gap-1">
                  {icons.map((name) => (
                    <button
                      key={name}
                      onClick={() => onSelect(name)}
                      title={name}
                      className="flex flex-col items-center justify-center gap-1 p-2 rounded-lg hover:bg-accent cursor-pointer transition-colors group"
                    >
                      <IconPreview name={name} />
                      <span className="text-[9px] text-muted-foreground group-hover:text-foreground truncate w-full text-center leading-tight">
                        {name}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </>
  );
}
